import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Clock, Package, Sparkle, MapPin, Warning, Printer } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AutocompleteInput } from '@/components/AutocompleteInput';
import { printMovementSlip } from '@/lib/printSlip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PutAwayPage({ user }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [housekeepingAlerts, setHousekeepingAlerts] = useState([]);
  const [formData, setFormData] = useState({
    item_code: '',
    qc_number: '',
    batch_number: '',
    reference: '',
    date_in: new Date().toISOString().split('T')[0],
    pallets: '',
    cartons: ''
  });

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('warehouse_token');
      const response = await axios.get(`${API}/tasks/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const putawayTasks = response.data.filter(t => t.task_type === 'putaway');
      setTasks(putawayTasks);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleSuggest = async () => {
    if (!formData.item_code || !formData.qc_number || !formData.batch_number || !formData.pallets || !formData.cartons) {
      toast.error('Please fill all required fields');
      return;
    }
    setSuggestionLoading(true);
    try {
      const token = localStorage.getItem('warehouse_token');
      const response = await axios.post(`${API}/putaway/suggest-location`, {
        ...formData,
        pallets: parseInt(formData.pallets),
        cartons: parseInt(formData.cartons)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestion(response.data);
      setSelectedLocationId(response.data.best_suggestion?.location_id || null);
      setHousekeepingAlerts(response.data.housekeeping_alerts || []);
      if (response.data.no_space) {
        toast.error('No location has sufficient space. Consider housekeeping first.');
      } else {
        toast.success('Location suggested successfully');
      }
    } catch (error) {
      toast.error('Failed to get suggestion');
    } finally {
      setSuggestionLoading(false);
    }
  };

  const handleConfirmPutAway = async () => {
    try {
      const token = localStorage.getItem('warehouse_token');
      const response = await axios.post(`${API}/putaway/create`, {
        ...formData,
        pallets: parseInt(formData.pallets),
        cartons: parseInt(formData.cartons),
        location_id: selectedLocationId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Put away completed - inventory updated');
      printMovementSlip('putaway', {
        ...formData,
        to_location_code: response.data.suggested_location?.location_code,
        suggestion_reason: response.data.suggested_location?.reason,
        task_id: response.data.task_id
      });
      setShowForm(false);
      setSuggestion(null);
      setSelectedLocationId(null);
      setFormData({
        item_code: '', qc_number: '', batch_number: '', reference: '',
        date_in: new Date().toISOString().split('T')[0],
        pallets: '', cartons: ''
      });
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create put away');
    }
  };

  const handlePrintTask = (task) => {
    printMovementSlip('putaway', {
      item_code: task.item_code,
      qc_number: task.qc_number,
      batch_number: task.batch_number,
      reference: task.reference,
      date_in: task.date_in,
      pallets: task.pallets || task.quantity,
      cartons: task.cartons,
      to_location_code: task.to_location?.code,
      suggestion_reason: task.suggestion_reason,
      task_id: task.id
    });
  };

  const handleCompleteTask = async (taskId) => {
    try {
      const token = localStorage.getItem('warehouse_token');
      await axios.post(`${API}/putaway/complete/${taskId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Put away completed');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  return (
    <div className="p-8" data-testid="putaway-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="heading text-3xl font-black text-[#09090B] mb-2" data-testid="putaway-title">
            Put Away
          </h1>
          <p className="text-[#52525B]">Register incoming stock with auto-suggested locations</p>
        </div>
        <button
          onClick={() => { setShowForm(!showForm); setSuggestion(null); }}
          className="btn-primary flex items-center gap-2"
          data-testid="new-putaway-button"
        >
          <Package size={18} weight="bold" />
          {showForm ? 'Close Form' : 'New Put Away'}
        </button>
      </div>

      {showForm && (
        <div className="card mb-6" data-testid="putaway-form">
          <h2 className="heading text-xl font-bold text-[#09090B] mb-4">Register Incoming Stock</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <AutocompleteInput
              id="pa_item_code"
              label="Item Code"
              field="item_code"
              value={formData.item_code}
              onChange={(v) => setFormData({ ...formData, item_code: v })}
              context={formData}
              placeholder="ITM-001"
              testId="putaway-item-code-input"
              required
            />
            <AutocompleteInput
              id="pa_qc_number"
              label="QC / Shade"
              field="qc_number"
              value={formData.qc_number}
              onChange={(v) => setFormData({ ...formData, qc_number: v })}
              context={formData}
              placeholder="QC-Blue-01"
              testId="putaway-qc-input"
              required
            />
            <AutocompleteInput
              id="pa_batch_number"
              label="Batch Number"
              field="batch_number"
              value={formData.batch_number}
              onChange={(v) => setFormData({ ...formData, batch_number: v })}
              context={formData}
              placeholder="BATCH-2026-001"
              testId="putaway-batch-input"
              required
            />
            <AutocompleteInput
              id="pa_reference"
              label="Reference"
              field="reference"
              value={formData.reference}
              onChange={(v) => setFormData({ ...formData, reference: v })}
              context={formData}
              placeholder="PO-12345 / REF-001"
              testId="putaway-reference-input"
            />
            <div>
              <Label htmlFor="date_in">Date In *</Label>
              <Input
                id="date_in"
                type="date"
                value={formData.date_in}
                onChange={(e) => setFormData({ ...formData, date_in: e.target.value })}
                data-testid="putaway-date-input"
              />
            </div>
            <div>
              <Label>Stock Quantity *</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="pallets"
                  type="number"
                  min="1"
                  value={formData.pallets}
                  onChange={(e) => setFormData({ ...formData, pallets: e.target.value })}
                  placeholder="10"
                  className="mono flex-1"
                  data-testid="putaway-pallets-input"
                />
                <span className="text-sm font-medium text-[#52525B] mono">plts</span>
                <span className="text-lg text-[#52525B]">×</span>
                <Input
                  id="cartons"
                  type="number"
                  min="1"
                  value={formData.cartons}
                  onChange={(e) => setFormData({ ...formData, cartons: e.target.value })}
                  placeholder="20"
                  className="mono flex-1"
                  data-testid="putaway-cartons-input"
                />
                <span className="text-sm font-medium text-[#52525B] mono">ctns</span>
              </div>
              {formData.pallets && formData.cartons && (
                <p className="text-xs text-[#52525B] mt-1 mono">
                  Total: {formData.pallets} plts × {formData.cartons} ctns = {parseInt(formData.pallets) * parseInt(formData.cartons)} cartons
                </p>
              )}
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <button
              onClick={handleSuggest}
              disabled={suggestionLoading}
              className="btn-secondary flex items-center gap-2"
              data-testid="suggest-location-button"
            >
              <Sparkle size={18} weight="fill" />
              {suggestionLoading ? 'Analyzing...' : 'Suggest Location'}
            </button>
            {suggestion && !suggestion.no_space && (
              <>
                <button
                  onClick={handleConfirmPutAway}
                  className="btn-primary flex items-center gap-2"
                  data-testid="confirm-putaway-button"
                >
                  <CheckCircle size={18} weight="bold" />
                  Confirm & Update Inventory
                </button>
                <button
                  onClick={() => printMovementSlip('putaway', {
                    ...formData,
                    to_location_code: suggestion.best_suggestion.location_code,
                    suggestion_reason: suggestion.best_suggestion.reason
                  })}
                  className="btn-secondary flex items-center gap-2"
                  data-testid="print-putaway-slip-button"
                >
                  <Printer size={18} weight="bold" />
                  Print Slip
                </button>
              </>
            )}
          </div>

          {suggestion && suggestion.best_suggestion && (
            <div className="mt-6 p-4 bg-[#16A34A]/10 border-2 border-[#16A34A] rounded-md" data-testid="suggestion-card">
              <p className="text-xs font-bold text-[#16A34A] uppercase tracking-wide mb-3">Select a location for put away</p>
              
              {/* All locations as radio-selectable cards */}
              <div className="space-y-2">
                {suggestion.suggestions.map((s, i) => {
                  const isSelected = selectedLocationId === s.location_id;
                  const isRecommended = i === 0;
                  return (
                    <button
                      key={s.location_id}
                      type="button"
                      onClick={() => setSelectedLocationId(s.location_id)}
                      className={`w-full text-left p-3 rounded border-2 transition-all duration-150 ${
                        isSelected
                          ? 'bg-white border-[#16A34A] shadow-sm'
                          : 'bg-white/60 border-[#E4E4E7] hover:border-[#16A34A]/50'
                      }`}
                      data-testid={`location-option-${i}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-1 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                          isSelected ? 'border-[#16A34A] bg-[#16A34A]' : 'border-[#A1A1AA]'
                        }`}>
                          {isSelected && <div className="w-1.5 h-1.5 bg-white rounded-full"></div>}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2">
                              <span className="mono text-base font-black text-[#09090B]">{s.location_code}</span>
                              {isRecommended && (
                                <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-sm bg-[#16A34A] text-white uppercase tracking-wide">
                                  Recommended
                                </span>
                              )}
                            </div>
                            <span className="text-xs mono text-[#52525B]">{s.available_capacity} available</span>
                          </div>
                          <p className="text-xs text-[#52525B]">{s.reason}</p>
                          <div className="flex gap-4 mt-1 text-xs text-[#52525B]">
                            <span>Zone: <span className="mono font-bold text-[#09090B]">{s.zone}</span></span>
                            <span>Capacity: <span className="mono font-bold text-[#09090B]">{s.current_capacity}/{s.total_capacity}</span></span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {suggestion && suggestion.no_space && (
            <div className="mt-6 p-4 bg-[#DC2626]/10 border-2 border-[#DC2626] rounded-md" data-testid="no-space-alert">
              <div className="flex items-start gap-3">
                <Warning size={24} weight="fill" className="text-[#DC2626] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="heading text-lg font-bold text-[#09090B]">No Available Space</h3>
                  <p className="text-sm text-[#52525B] mt-1">
                    No location has sufficient capacity for {formData.pallets || 0} plts × {formData.cartons || 0} ctns.
                    Please run housekeeping to consolidate stock first.
                  </p>
                  <a href="/housekeeping" className="inline-block mt-3 btn-primary text-sm">
                    Go to Housekeeping
                  </a>
                </div>
              </div>
            </div>
          )}

          {housekeepingAlerts.length > 0 && (
            <div className="mt-4 p-4 bg-[#F59E0B]/10 border border-[#F59E0B] rounded-md" data-testid="housekeeping-alert">
              <div className="flex items-start gap-3">
                <Warning size={20} weight="fill" className="text-[#F59E0B] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-[#09090B] mb-1">Housekeeping Opportunity</h4>
                  {housekeepingAlerts.map((alert, i) => (
                    <p key={i} className="text-sm text-[#52525B]">{alert.message}</p>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="card bg-[#F59E0B]/5 border-[#F59E0B]/20">
          <div className="flex items-center gap-3">
            <Clock size={24} className="text-[#F59E0B]" weight="duotone" />
            <div>
              <p className="text-sm text-[#52525B]">Pending</p>
              <p className="heading text-2xl font-black text-[#09090B]" data-testid="pending-count">{pendingTasks.length}</p>
            </div>
          </div>
        </div>
        <div className="card bg-[#16A34A]/5 border-[#16A34A]/20">
          <div className="flex items-center gap-3">
            <CheckCircle size={24} className="text-[#16A34A]" weight="duotone" />
            <div>
              <p className="text-sm text-[#52525B]">Completed</p>
              <p className="heading text-2xl font-black text-[#09090B]" data-testid="completed-count">{completedTasks.length}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <Package size={24} className="text-[#007AFF]" weight="duotone" />
            <div>
              <p className="text-sm text-[#52525B]">Total Tasks</p>
              <p className="heading text-2xl font-black text-[#09090B]" data-testid="total-count">{tasks.length}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {pendingTasks.length > 0 && (
          <div>
            <h2 className="heading text-xl font-bold text-[#09090B] mb-4">Pending Put Away Tasks</h2>
            <div className="space-y-3">
              {pendingTasks.map((task) => (
                <div key={task.id} className="card hover:border-[#FF3B30] transition-colors duration-150" data-testid="task-card">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <span className="px-2 py-1 text-xs font-medium rounded-sm bg-[#F59E0B] text-white">
                          {task.priority?.toUpperCase() || 'NORMAL'}
                        </span>
                        {task.item_code && (
                          <span className="mono text-sm font-bold text-[#007AFF]" data-testid="task-item-code">
                            {task.item_code}
                          </span>
                        )}
                        <span className="mono text-sm font-bold text-[#09090B]" data-testid="task-sku">{task.item?.sku}</span>
                      </div>
                      <p className="text-base font-medium text-[#09090B] mb-3" data-testid="task-item-name">{task.item?.name}</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-[#52525B] text-xs">Stock Quantity</p>
                          <p className="mono font-bold text-[#09090B]" data-testid="task-quantity">
                            {task.pallets || task.quantity} plts{task.cartons ? ` × ${task.cartons} ctns` : ''}
                          </p>
                        </div>
                        <div>
                          <p className="text-[#52525B] text-xs">To Location</p>
                          <p className="mono font-bold text-[#16A34A]" data-testid="task-location">{task.to_location?.code || 'N/A'}</p>
                        </div>
                        {task.qc_number && (
                          <div>
                            <p className="text-[#52525B] text-xs">QC / Shade</p>
                            <p className="mono font-bold text-[#09090B]" data-testid="task-qc">{task.qc_number}</p>
                          </div>
                        )}
                        {task.batch_number && (
                          <div>
                            <p className="text-[#52525B] text-xs">Batch</p>
                            <p className="mono font-bold text-[#09090B]" data-testid="task-batch">{task.batch_number}</p>
                          </div>
                        )}
                        {task.reference && (
                          <div>
                            <p className="text-[#52525B] text-xs">Reference</p>
                            <p className="mono font-bold text-[#09090B]" data-testid="task-reference">{task.reference}</p>
                          </div>
                        )}
                      </div>
                      {task.suggestion_reason && (
                        <div className="mt-3 p-2 bg-[#F4F4F5] rounded text-xs text-[#52525B]">
                          <Sparkle size={12} weight="fill" className="inline mr-1 text-[#F59E0B]" />
                          {task.suggestion_reason}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handlePrintTask(task)}
                      className="btn-secondary flex-shrink-0"
                      data-testid="putaway-task-print-button"
                      title="Print put away slip"
                    >
                      <Printer size={18} weight="bold" />
                    </button>
                    <button
                      onClick={() => handleCompleteTask(task.id)}
                      className="btn-primary flex-shrink-0"
                      data-testid="task-complete-button"
                    >
                      Complete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {pendingTasks.length === 0 && !showForm && (
          <div className="card text-center py-12" data-testid="no-pending-tasks">
            <CheckCircle size={48} className="mx-auto text-[#16A34A] mb-4" weight="duotone" />
            <h3 className="heading text-xl font-bold text-[#09090B] mb-2">All caught up!</h3>
            <p className="text-[#52525B] mb-4">No pending put away tasks. Click "New Put Away" to register incoming stock.</p>
          </div>
        )}

        {completedTasks.length > 0 && (
          <div>
            <h2 className="heading text-xl font-bold text-[#09090B] mb-4">Recently Completed</h2>
            <div className="space-y-3">
              {completedTasks.slice(0, 5).map((task) => (
                <div key={task.id} className="card opacity-60" data-testid="completed-task-card">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={20} className="text-[#16A34A]" weight="fill" />
                    <span className="mono text-sm font-bold text-[#09090B]">{task.item_code || task.item?.sku}</span>
                    <span className="text-sm text-[#52525B] ml-auto">
                      <span className="mono">{task.pallets || task.quantity} plts{task.cartons ? ` × ${task.cartons} ctns` : ''}</span> → <span className="mono">{task.to_location?.code}</span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
